/**
 * Chat service for creating and managing chat documents.
 *
 * Provides a unified interface for chat creation using the backend registry pattern.
 * Uses Firestore-safe payloads (no undefined values).
 * Peer display names come from `getPublicUserProfiles` (not direct `users` reads).
 *
 * @example
 * ```typescript
 * import chatService from '@features/messaging/services/chatService';
 *
 * // Create a chat for a new match
 * const chatId = await chatService.createChatForMatch('user1', 'user2');
 * ```
 */

import { getChatIdForPair } from "@features/messaging/utils/chatParticipants";
import type { Chat, ChatMessage } from "@fitties/shared";
import { auth } from "@infrastructure/config/firebase";
import { backendRegistry } from "@infrastructure/providers/backendRegistry";
import logger from "@logger";

import { fetchPublicUserProfiles } from "@shared/services/publicUserProfilesService";
import { arrayUnion, orderBy, serverTimestamp, where } from "firebase/firestore";

/**
 * Find an existing chat for a user pair using the deterministic chat ID.
 * Single document read — O(1) instead of a full collection scan.
 */
async function getChatForPair(userId1: string, userId2: string): Promise<Chat | null> {
  try {
    const chatId = getChatIdForPair(userId1, userId2);
    const chat = await backendRegistry.database.getDocument<Chat>("chats", chatId);
    return chat ? { ...chat, id: chatId } : null;
  } catch (error) {
    logger.error("Error finding existing chat:", error);
    return null;
  }
}

const chatService = {
  /**
   * Find an existing chat between two users.
   * Returns the chat document with id, or null.
   */
  getChatForPair,

  /**
   * Subscribe to messages for a chat, ordered by timestamp ascending.
   * Returns an unsubscribe function.
   */
  subscribeToMessages(
    chatId: string,
    callback: (docs: ChatMessage[]) => void,
    onError?: (error: Error) => void
  ): () => void {
    return backendRegistry.database.subscribeToCollection<ChatMessage>(
      "chatMessages",
      callback,
      [where("chat_id", "==", chatId), orderBy("timestamp")],
      onError
    );
  },

  /**
   * Send a message in a chat and update the chat's denormalized last-message fields.
   */
  async sendMessage(chatId: string, senderUid: string, text: string): Promise<void> {
    const chat = await backendRegistry.database.getDocument<Chat>("chats", chatId);
    if (!chat) {
      throw new Error("Chat not found");
    }

    const timestamp = serverTimestamp();

    await backendRegistry.database.addDocument("chatMessages", {
      sender_uid: senderUid,
      chat_id: chatId,
      text,
      timestamp,
    });

    await backendRegistry.database.updateDocument("chats", chatId, {
      last_message_text: text,
      last_message_sent_timestamp: timestamp,
      last_message_sent_by_uid: senderUid,
      last_message_seen_by_uids: [senderUid],
    });
  },

  /**
   * Create a chat for a new match between two users.
   * Returns existing chat ID if one already exists; otherwise creates a new chat.
   * Chat document is written at the deterministic ID from getChatIdForPair — idempotent.
   *
   * Display names are loaded via public profiles (Cloud Function), not full user docs.
   * `shared_event_id` is left null on this client path (`handleLike` sets it on mutual match).
   * Only the signed-in caller's own `chat_partner_uids` is updated (peer writes are rules-blocked).
   */
  async createChatForMatch(userId1: string, userId2: string): Promise<string> {
    try {
      logger.info("Creating chat for match", { userId1, userId2 });

      const chatId = getChatIdForPair(userId1, userId2);
      const existingChat = await backendRegistry.database.getDocument<Chat>("chats", chatId);

      if (!existingChat) {
        const profiles = await fetchPublicUserProfiles([userId1, userId2]);
        const byUid = new Map(profiles.map((p) => [p.uid, p]));
        const user1 = byUid.get(userId1);
        const user2 = byUid.get(userId2);
        const displayNames = [user1?.display_name ?? "", user2?.display_name ?? ""];

        const chatData: Record<string, unknown> = {
          participant_uids: [userId1, userId2],
          participant_display_names: displayNames,
          last_message_seen_by_uids: [],
          is_blocked: false,
          created_time: serverTimestamp(),
          shared_event_id: null,
        };

        await backendRegistry.database.setDocument("chats", chatId, chatData);
        logger.info("Chat created successfully", { chatId, userId1, userId2 });
      } else {
        logger.info("Chat already exists", { chatId });
      }

      // Only update the caller's own chat_partner_uids (peer updates are CF-only / rules-blocked)
      const callerUid = auth.currentUser?.uid;
      if (callerUid === userId1) {
        await backendRegistry.database.updateDocument("users", userId1, {
          chat_partner_uids: arrayUnion(userId2),
        });
      } else if (callerUid === userId2) {
        await backendRegistry.database.updateDocument("users", userId2, {
          chat_partner_uids: arrayUnion(userId1),
        });
      }

      return chatId;
    } catch (error) {
      logger.error("Error creating chat for match:", error);
      throw error;
    }
  },
};

export default chatService;
